import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { productsApi, workflowApi, getBackendOrigin } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import ProductQrStyleFields, {
  emptyProductQrStyle,
  productQrStyleFromApi,
  buildQrStylePayload,
} from '../components/ProductQrStyleFields';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Plus, PencilSimple, Trash, MagnifyingGlass, CircleNotch, X, UploadSimple, DownloadSimple, Package, QrCode, TreeStructure } from '@phosphor-icons/react';
import { formatDate, formatApiErrorDetail } from '../lib/utils';
import { toast } from 'sonner';

const DEFAULT_STAGES = ["Received", "Inspected", "Packed", "Shipped", "Delivered"];

function computeExpiryDate(mfgDate, shelfLifeDays) {
  if (!mfgDate || !shelfLifeDays) return '';
  const d = new Date(`${mfgDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + Number(shelfLifeDays));
  return d.toISOString().slice(0, 10);
}

function emptyFormData() {
  return {
    name: '',
    sku: '',
    description: '',
    batch_number: '',
    manufacturing_date: '',
    expiry_date: '',
    shelf_life_days: '',
    auto_expiry: false,
    category: '',
    scan_stages: [...DEFAULT_STAGES],
    workflow_template_id: '',
    qr_style: emptyProductQrStyle(),
  };
}

function qrStyleSummary(style) {
  if (!style?.color_mode) return null;
  const mode = style.color_mode;
  if (mode === 'multi_color') {
    const palette = style.palette_mode || 'spiral';
    return palette === 'spiral' ? 'Spiral QR' : 'Multi-color QR';
  }
  const labels = {
    grayscale: 'Grayscale',
    black_white: 'Black & white',
    black_gray: 'Gray dots',
    color: 'Color',
  };
  return labels[mode] || mode;
}

function productImageSrc(product) {
  const url = product?.image_url;
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${getBackendOrigin()}${url.startsWith('/') ? url : `/${url}`}`;
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [formData, setFormData] = useState(emptyFormData());
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [newStage, setNewStage] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importSheet, setImportSheet] = useState('');
  const [sheetOptions, setSheetOptions] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [workflowPromptOpen, setWorkflowPromptOpen] = useState(false);
  const [pendingProductId, setPendingProductId] = useState(null);
  const [promptTemplateId, setPromptTemplateId] = useState('');
  const [assigningWorkflow, setAssigningWorkflow] = useState(false);

  const loadProducts = useCallback(async () => {
    if (authLoading || !user || user === false) return;
    setLoading(true);
    try {
      const response = await productsApi.list();
      setProducts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    loadProducts();
    workflowApi.listTemplates()
      .then((res) => setWorkflowTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setWorkflowTemplates([]));
  }, [loadProducts]);

  const patchForm = (partial) => {
    setFormData((prev) => {
      const next = { ...prev, ...partial };
      if (next.auto_expiry && next.manufacturing_date && next.shelf_life_days) {
        next.expiry_date = computeExpiryDate(next.manufacturing_date, next.shelf_life_days);
      }
      return next;
    });
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        description: product.description || '',
        batch_number: product.batch_number || '',
        manufacturing_date: product.manufacturing_date || '',
        expiry_date: product.expiry_date || '',
        shelf_life_days: product.shelf_life_days ? String(product.shelf_life_days) : '',
        auto_expiry: Boolean(product.auto_expiry),
        category: product.category || '',
        scan_stages: product.scan_stages || [...DEFAULT_STAGES],
        workflow_template_id: product.workflow_template_id || '',
        qr_style: productQrStyleFromApi(product.qr_style),
      });
    } else {
      setEditingProduct(null);
      setFormData(emptyFormData());
    }
    setNewStage('');
    setDialogOpen(true);
  };

  const buildProductPayload = () => {
    const qrStylePayload = buildQrStylePayload(formData.qr_style);
    if (formData.qr_style?.color_mode === 'multi_color' && !qrStylePayload) {
      throw new Error('Multi-color QR requires at least 2 palette colors');
    }
    const payload = {
      name: formData.name,
      sku: formData.sku,
      description: formData.description || undefined,
      batch_number: formData.batch_number || undefined,
      manufacturing_date: formData.manufacturing_date || undefined,
      expiry_date: formData.expiry_date || undefined,
      category: formData.category || undefined,
      scan_stages: formData.scan_stages,
      max_scan_stages: formData.scan_stages.length,
      workflow_template_id: formData.workflow_template_id || null,
      auto_expiry: formData.auto_expiry,
      shelf_life_days: formData.shelf_life_days ? Number(formData.shelf_life_days) : null,
    };
    if (qrStylePayload) {
      payload.qr_style = qrStylePayload;
    } else if (editingProduct) {
      // Keep existing style if multi-color validation failed above (already thrown).
      payload.qr_style = null;
    }
    if (payload.auto_expiry && payload.manufacturing_date && payload.shelf_life_days) {
      payload.expiry_date = computeExpiryDate(payload.manufacturing_date, payload.shelf_life_days);
    }
    if (!payload.batch_number) delete payload.batch_number;
    return payload;
  };

  const openCreateWorkflowForProduct = (productId, productName) => {
    const params = new URLSearchParams({
      assignProduct: productId,
      name: productName ? `${productName} workflow` : 'Product workflow',
    });
    navigate(`/workflows/new?${params.toString()}`);
  };

  const handlePromptAssign = async () => {
    if (!pendingProductId || !promptTemplateId) {
      toast.error('Select a workflow to assign');
      return;
    }
    setAssigningWorkflow(true);
    try {
      await workflowApi.assignToProduct(pendingProductId, promptTemplateId);
      toast.success('Workflow assigned to product');
      setWorkflowPromptOpen(false);
      setPendingProductId(null);
      loadProducts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to assign workflow');
    } finally {
      setAssigningWorkflow(false);
    }
  };

  const handlePromptCreateWorkflow = () => {
    const product = products.find((p) => p.id === pendingProductId);
    setWorkflowPromptOpen(false);
    openCreateWorkflowForProduct(pendingProductId, product?.name || formData.name);
  };

  const handleSave = async () => {
    if (
      formData.manufacturing_date &&
      formData.expiry_date &&
      formData.expiry_date < formData.manufacturing_date
    ) {
      toast.error('Expiry Date cannot be earlier than Manufacturing Date');
      return;
    }
    setSaving(true);
    try {
      const payload = buildProductPayload();
      let saved;
      if (editingProduct) {
        const res = await productsApi.update(editingProduct.id, payload);
        saved = res.data;
      } else {
        const res = await productsApi.create(payload);
        saved = res.data;
      }
      setDialogOpen(false);
      loadProducts();
      if (!formData.workflow_template_id) {
        setPendingProductId(saved.id);
        setPromptTemplateId('');
        setWorkflowPromptOpen(true);
        toast.success(editingProduct ? 'Product updated' : 'Product created');
        return;
      }
      toast.success(editingProduct ? 'Product updated successfully' : 'Product created successfully');
    } catch (error) {
      toast.error(error.message || formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await productsApi.delete(id);
      toast.success('Product deleted successfully');
      loadProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const addStage = () => {
    if (newStage.trim() && !formData.scan_stages.includes(newStage.trim())) {
      setFormData((prev) => ({ ...prev, scan_stages: [...prev.scan_stages, newStage.trim()] }));
      setNewStage('');
    }
  };

  const removeStage = (index) => {
    setFormData({
      ...formData,
      scan_stages: formData.scan_stages.filter((_, i) => i !== index),
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await productsApi.downloadImportTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'product-import-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    setImportFile(file || null);
    setImportPreview(null);
    setSheetOptions([]);
    setImportSheet('');
    if (!file) return;
    try {
      const res = await productsApi.previewImport(file);
      setImportPreview(res.data);
      const options = res.data.sheet_options || [];
      setSheetOptions(options);
      setImportSheet(res.data.selected_sheet || options[0] || '');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleImportCatalog = async () => {
    if (!importFile) {
      toast.error(t('products.importChooseFile'));
      return;
    }
    setImporting(true);
    try {
      const res = await productsApi.importCatalog(importFile, {
        sheetName: importSheet || undefined,
        skipDuplicates,
      });
      toast.success(t('products.importSuccess', { count: res.data.imported }));
      if (res.data.skipped) {
        toast.info(t('products.importSkipped', { count: res.data.skipped }));
      }
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      loadProducts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase()) ||
      p.category?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="products-page">
        <PageHeader
          title={t('products.title')}
          description={`${t('products.subtitle')}${!loading && products.length > 0 ? ` (${products.length})` : ''}`}
          actions={(
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setImportOpen(true);
                  setImportFile(null);
                  setImportPreview(null);
                  setSheetOptions([]);
                }}
                className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                data-testid="import-catalog-btn"
              >
                <UploadSimple className="w-4 h-4 mr-2" />
                {t('products.importCatalog')}
              </Button>
              <Button
                onClick={() => handleOpenDialog()}
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('products.addProduct')}
              </Button>
            </div>
          )}
        />

        {/* Search */}
        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardContent className="p-4">
            <div className="relative">
              <MagnifyingGlass className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-9 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}`}
                data-testid="search-products-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <CircleNotch className="w-8 h-8 animate-spin text-brand-500" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <CardContent className="py-16 text-center">
              <Package className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>No products found</p>
              {!search && (
                <p className={`text-sm mt-2 max-w-md mx-auto ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  If you expected the KSDL catalog, use <strong>Import catalog</strong> above or restore the local bundle, then sign out and sign in again.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map((product) => {
              const imageSrc = productImageSrc(product);
              return (
                <Card
                  key={product.id}
                  className={`group overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 hover:border-brand-500/40'
                      : 'bg-white border-zinc-200 hover:border-brand-300'
                  }`}
                  data-testid={`product-tile-${product.id}`}
                >
                  <div className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden ${isDark ? 'bg-zinc-950/50' : 'bg-slate-50'}`}>
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={product.name}
                        className="w-full h-full object-contain p-3"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <Package className={`w-16 h-16 ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
                    )}
                    {product.category && (
                      <span className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-medium max-w-[80%] truncate ${
                        isDark ? 'bg-zinc-800/90 text-zinc-300 border border-zinc-700' : 'bg-white/90 text-zinc-600 border border-zinc-200'
                      }`}>
                        {product.category}
                      </span>
                    )}
                  </div>

                  <CardContent className="p-4 space-y-3">
                    <div className="min-w-0">
                      <h3 className={`text-sm font-semibold line-clamp-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                        {product.name}
                      </h3>
                      <p className={`text-xs font-mono mt-1 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                        {product.sku}
                      </p>
                      {product.batch_number && (
                        <p className={`text-xs font-mono mt-0.5 ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>
                          {product.batch_number}
                        </p>
                      )}
                    </div>

                    {product.description && (
                      <p className={`text-xs line-clamp-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        {product.description}
                      </p>
                    )}

                    <div className={`flex items-center justify-between text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      <span className="flex items-center gap-1">
                        <QrCode className="w-3.5 h-3.5" />
                        {product.qr_count || 0} {t('products.qrCount')}
                      </span>
                      <span>{product.scan_stages?.length || 0} {t('products.stages')}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {product.workflow_template_id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/workflows/${product.workflow_template_id}`)}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/25' : 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'}`}
                          title={t('products.editAssignedWorkflow')}
                        >
                          <TreeStructure className="w-3 h-3" />
                          {product.workflow_name || t('products.workflow')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => navigate(`/workflows?assignProduct=${product.id}`)}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'}`}
                        >
                          <TreeStructure className="w-3 h-3" />
                          {t('products.noWorkflow')}
                        </button>
                      )}
                      {qrStyleSummary(product.qr_style) && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${isDark ? 'bg-zinc-800 text-zinc-300 border border-zinc-700' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                          {qrStyleSummary(product.qr_style)}
                        </span>
                      )}
                    </div>

                    <div className={`text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                      {formatDate(product.created_at)}
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenDialog(product)}
                        className={`flex-1 h-8 text-xs ${isDark ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' : ''}`}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <PencilSimple className="w-3.5 h-3.5 mr-1.5" />
                        {t('common.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(product.id)}
                        className="h-8 text-xs text-red-500 border-red-500/30 hover:bg-red-500/10 hover:text-red-600"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white border-zinc-200 text-zinc-900'} max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle className={`font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                {editingProduct ? t('products.editProduct') : t('products.addProduct')}
              </DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {editingProduct ? t('products.editDialogDesc') : t('products.addDialogDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.productName')} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => patchForm({ name: e.target.value })}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.sku')} *</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => patchForm({ sku: e.target.value })}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-sku-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('common.description')}</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => patchForm({ description: e.target.value })}
                  className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                  data-testid="product-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.category')}</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => patchForm({ category: e.target.value })}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-category-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                    {t('products.batchNumber')}
                    <span className={`text-xs ml-1 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>({t('products.autoIfEmpty')})</span>
                  </Label>
                  <Input
                    value={formData.batch_number}
                    onChange={(e) => patchForm({ batch_number: e.target.value })}
                    placeholder="e.g. OMN-00001"
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-batch-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.mfgDate')}</Label>
                  <Input
                    type="date"
                    value={formData.manufacturing_date}
                    onChange={(e) => patchForm({ manufacturing_date: e.target.value })}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-mfg-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.expiryDate')}</Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    min={formData.manufacturing_date || undefined}
                    onChange={(e) => patchForm({ expiry_date: e.target.value, auto_expiry: false })}
                    disabled={formData.auto_expiry}
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-expiry-date-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.shelfLifeDays')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.shelf_life_days}
                    onChange={(e) => patchForm({ shelf_life_days: e.target.value })}
                    placeholder="e.g. 365"
                    className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}
                    data-testid="product-shelf-life-input"
                  />
                </div>
                <label className={`flex items-center gap-2 text-sm cursor-pointer pb-2 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>
                  <input
                    type="checkbox"
                    checked={formData.auto_expiry}
                    onChange={(e) => patchForm({ auto_expiry: e.target.checked })}
                    data-testid="product-auto-expiry-checkbox"
                  />
                  {t('products.autoExpiry')}
                </label>
              </div>
              {formData.auto_expiry && (
                <p className={`text-xs -mt-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('products.autoExpiryHint')}</p>
              )}

              <div className={`rounded-lg border p-4 space-y-3 ${isDark ? 'border-zinc-700 bg-zinc-800/30' : 'border-slate-200 bg-slate-50'}`}>
                <div>
                  <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>{t('products.workflow')}</Label>
                  <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('products.workflowRequiredHint')}</p>
                </div>
                <Select
                  value={formData.workflow_template_id || '__none__'}
                  onValueChange={(v) => patchForm({ workflow_template_id: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="product-workflow-select">
                    <SelectValue placeholder={t('products.workflowNone')} />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                    <SelectItem value="__none__">{t('products.workflowNone')}</SelectItem>
                    {workflowTemplates.map((tpl) => (
                      <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                    onClick={() => {
                      const productId = editingProduct?.id;
                      const params = new URLSearchParams({
                        name: formData.name ? `${formData.name} workflow` : 'Product workflow',
                      });
                      if (productId) params.set('assignProduct', productId);
                      navigate(`/workflows/new?${params.toString()}`);
                    }}
                    data-testid="product-create-workflow-btn"
                  >
                    <TreeStructure className="w-4 h-4 mr-1.5" />
                    {t('products.createWorkflowForProduct')}
                  </Button>
                  {formData.workflow_template_id ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className={isDark ? 'border-zinc-700 text-zinc-300' : ''}
                      onClick={() => navigate(`/workflows/${formData.workflow_template_id}`)}
                      data-testid="product-edit-workflow-btn"
                    >
                      <PencilSimple className="w-4 h-4 mr-1.5" />
                      {t('products.editAssignedWorkflow')}
                    </Button>
                  ) : null}
                </div>
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{t('products.workflowHint')}</p>
              </div>

              <ProductQrStyleFields
                value={formData.qr_style}
                onChange={(qr_style) => patchForm({ qr_style })}
                isDark={isDark}
              />

              {/* Scan Stages */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                  {t('products.scanStages')}
                  <span className={`text-xs ml-1 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                    ({formData.scan_stages.length} stages)
                  </span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {formData.scan_stages.map((stage, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-sm ${
                        isDark ? 'bg-zinc-800 text-zinc-300 border border-zinc-700' : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}
                    >
                      <span className={`text-xs font-mono mr-1 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{index + 1}.</span>
                      {stage}
                      <button
                        type="button"
                        onClick={() => removeStage(index)}
                        className={`ml-1 p-0.5 rounded hover:bg-red-500/10 ${isDark ? 'text-zinc-500 hover:text-red-400' : 'text-slate-400 hover:text-red-500'}`}
                        data-testid={`remove-stage-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newStage}
                    onChange={(e) => setNewStage(e.target.value)}
                    placeholder={t('products.addStage')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStage())}
                    className={`flex-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'}`}
                    data-testid="add-stage-input"
                  />
                  <Button
                    type="button"
                    onClick={addStage}
                    variant="outline"
                    size="sm"
                    className={isDark ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}
                    data-testid="add-stage-btn"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className={isDark ? 'border-zinc-700 text-zinc-300 hover:bg-zinc-800' : 'border-zinc-300 text-zinc-700 hover:bg-zinc-100'}
                data-testid="cancel-product-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name || !formData.sku}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="save-product-btn"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={workflowPromptOpen} onOpenChange={setWorkflowPromptOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-md`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{t('products.assignWorkflowPromptTitle')}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {t('products.assignWorkflowPromptDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {workflowTemplates.length > 0 ? (
                <div className="space-y-2">
                  <Label>{t('workflows.workflowLabel')}</Label>
                  <Select value={promptTemplateId} onValueChange={setPromptTemplateId}>
                    <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                      <SelectValue placeholder={t('workflows.assignTemplatePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                      {workflowTemplates.map((tpl) => (
                        <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handlePromptAssign}
                    disabled={assigningWorkflow || !promptTemplateId}
                    className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                    data-testid="prompt-assign-workflow-btn"
                  >
                    {assigningWorkflow ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('products.assignExistingWorkflow')}
                  </Button>
                </div>
              ) : (
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('workflows.noTemplates')}</p>
              )}
              <Button
                type="button"
                variant="outline"
                className={`w-full ${isDark ? 'border-zinc-700' : ''}`}
                onClick={handlePromptCreateWorkflow}
                data-testid="prompt-create-workflow-btn"
              >
                <TreeStructure className="w-4 h-4 mr-2" />
                {t('products.createWorkflowForProduct')}
              </Button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setWorkflowPromptOpen(false)}>
                {t('products.skipWorkflowForNow')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className={`${isDark ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white'} max-w-lg`}>
            <DialogHeader>
              <DialogTitle className="font-heading">{t('products.importTitle')}</DialogTitle>
              <DialogDescription className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>
                {t('products.importDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={handleDownloadTemplate}
                  className={isDark ? 'border-zinc-700 text-zinc-300' : ''}>
                  <DownloadSimple className="w-4 h-4 mr-2" />
                  {t('products.downloadTemplate')}
                </Button>
              </div>
              <div className="space-y-2">
                <Label>{t('products.importChooseFile')}</Label>
                <Input type="file" accept=".csv,.xlsx,.xlsm" onChange={handleImportFileChange}
                  className={isDark ? 'bg-zinc-800 border-zinc-700' : ''} data-testid="import-file-input" />
              </div>
              {sheetOptions.length > 1 && (
                <div className="space-y-2">
                  <Label>{t('products.importSheet')}</Label>
                  <select
                    value={importSheet}
                    onChange={(e) => setImportSheet(e.target.value)}
                    className={`w-full rounded-md border px-3 py-2 text-sm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300'}`}
                    data-testid="import-sheet-select"
                  >
                    {sheetOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} />
                {t('products.skipDuplicates')}
              </label>
              {importPreview && (
                <div className={`rounded-md border p-3 text-sm ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-zinc-200 bg-slate-50'}`}>
                  <p>{importPreview.row_count} product row(s) ready to import</p>
                  {importPreview.errors?.length > 0 && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                      {importPreview.errors.length} parse warning(s)
                    </p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleImportCatalog} disabled={importing || !importFile}
                className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="confirm-import-btn">
                {importing ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('products.importProducts')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
